<template>
    <div>
        <div v-if="items && items.length === 0 && ! loading" class="flex items-center justify-center mb-5 pt-5">
            <h1 v-if="search" class="font-normal text-center text-4xl leading-none">
                No items found matching<br>
                <q v-text="search" class="italic opacity-75"></q>
            </h1>
            <h1 v-else class="font-normal text-center text-4xl leading-none">
                No items found matching the selected filters
            </h1>
        </div>

        <div v-if="suggestions && suggestions.length !== 0 && ! loading" class="flex items-center justify-center text-center mb-5">
            <h1 v-if="search" class="text-2xl leading-none">
                Did you mean
                <span v-for="suggestion in suggestions" :key="suggestion">
                    <a :href="`${requestPath}?filter[search]=${suggestion}`" class="text-orange-500 hover:text-orange-600">
                        <br>
                        <q v-text="suggestion" class="italic opacity-75"></q>
                    </a>
                </span>
            </h1>
        </div>

        <div v-if="items && items.length > 0" :class="[
            type === 'compact' ? 'grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4' : 'grid grid-cols-1 gap-y-3'
        ]">
            <div :is="component" :key="item.id" :item="item" :id="item.sku" v-for="item in itemsWithPromos"></div>
        </div>
    </div>
</template>

<script>
    import UCompactResult from './Result/Compact.vue';
    import UDetailedResult from './Result/Detailed.vue';
    import Loader from "../Icons/Loader.vue";

    export default {
        components: {
            Loader,
            UCompactResult,
            UDetailedResult,
        },

        props: {
            search: {
                type: String,
                required: false,
                default: ''
            },

            items: {
                type: Array,
                required: true,
            },

            suggestions: {
                type: Array,
                required: false,
            },

            requestPath: {
                type: String,
                required: false,
                default() {
                    return `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
                },
            },

            pagination: {
                type: Object,
                required: false,
                default() {
                    return null;
                },
            },

            loading: {
                type: Boolean,
                required: false,
                default() {
                    return false;
                },
            },

            type: {
                type: String,
                required: false,
                default: 'compact',
            },
        },

        data() {
            return {
                focusedIndex: null,
                promoVariants: [
                    'promo-tile-quotation',
                    'promo-tile-calculator',
                    'promo-tile-matching',
                ],
            };
        },

        watch: {
            items() {
                this.$nextTick(() => {
                    if (null === this.focusedIndex) {
                        return;
                    }

                    // Grab the current brick so we can get its sku
                    const brick = this.items[this.focusedIndex];

                    // Cache the index we have used
                    const index = this.focusedIndex;

                    // Reset the index so we don't keep re-triggering this code
                    this.focusedIndex = null;

                    if (! brick) {
                        return;
                    }

                    // Get the brick result element based on its sku
                    const element = 0 === index ? this.$parent.$el : window.document.querySelector(`#${brick.sku}`);

                    if (element === null) {
                        return;
                    }

                    // We need the bounds of the element to figure out its offsets
                    const elBounds = element.getBoundingClientRect();

                    // The bounds of the body to figure out where we are in the screen
                    const bodyBounds = document.body.getBoundingClientRect();

                    // The scroll position of the element is el.top - body.top,
                    // we add 10px of padding so its not kissing the header
                    // or the top of the browser window frame
                    let to = elBounds.top - bodyBounds.top - 10;

                    // We only need to offset by the header height if it is
                    // using the fixed header (ie: at the breakpoint)
                    const header = window.document.querySelector('#header');
                    if (header !== null) {
                        const position = getComputedStyle(header).position;
                        if (position === 'fixed' || position === 'sticky') {
                            to -= header.clientHeight;
                        }
                    }

                    window.scroll({
                        top: to,
                        left: 0,
                        behavior: 'smooth',
                    });
                });
            },
        },

        computed: {
            component() {
                switch (this.type) {
                    case 'detailed':
                        return UDetailedResult;

                    case 'compact':
                    default:
                        return UCompactResult;
                }
            },

            itemsWithPromos() {
                const output = [];

                // Place promotional tile after every Nth brick record.
                // e.g. If 'interval' is 7, every 8th tile would be a promotional tile.
                // The "Brick Library" pagination values assume this interval is "7".
                const interval = 7;

                let variantIndex = 0;
                this.items.forEach((item, index) => {
                    output.push(item);

                    if ((index + 1) % interval === 0) {
                        const promoTile = {
                            isPromotion: true,
                            promotionVariant: this.promoVariants[variantIndex % this.promoVariants.length],
                        };
                        output.push(promoTile);
                        variantIndex++;
                    }
                });

                return output;
            },
        },

        methods: {
            focus(index) {
                this.focusedIndex = index;
            },
        },
    };
</script>
